import { MenuItem, Tooltip } from '@blueprintjs/core';
import {
  lock_hooks,
  useCancelQuote,
  useClientEmail,
  useLoanQuotes,
  useQuoteStatusUtils,
} from '@axo/shared/data-access/hooks';
import { loan_quote } from '@axo/shared/data-access/types';
import styles from './CancelAllOffers.module.css';
import { useConfirmDialog } from '../../../hooks/utils/useConfirmDialog';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import { useContext } from 'react';
import { getLocalPartOfEmail } from '../../../utils/getLocalPartOfEmail';
import { IDialog } from '../../ConfirmDialogProvider/ConfirmDialogProvider';
import { EventCodeACE } from '../../EventLogWrapper/event-code.tsx';
import {
  KnownResourceType,
  useEventLogger,
} from '@axo/shared/data-access/event-log';

export type ICancelAllOffers = {
  marketCountry: string;
  applicationID: string;
  cancelledBy: 'Axo' | 'Customer';
};

export function CancelAllOffers({
  marketCountry,
  applicationID,
  cancelledBy,
}: ICancelAllOffers) {
  const loanQuotes = useLoanQuotes(applicationID);
  const cancelQuoteByAxo = useCancelQuote(applicationID, marketCountry);
  const {
    state: { user },
  } = useContext(DataAccessContext);

  const { isInQuoteStatusGroup } = useQuoteStatusUtils();
  const log = useEventLogger({
    Type: KnownResourceType.LoanApplication,
    ID: applicationID,
  });
  const eventLogType =
    cancelledBy === 'Axo'
      ? EventCodeACE.OfferCancelledByAxo
      : EventCodeACE.OfferCancelledOnCustomerBehalf;

  function canBeCancelled(quote: loan_quote.BankQuote) {
    const isCancelled = isInQuoteStatusGroup(
      quote.Status,
      loan_quote.QuoteStatusGroup.Cancelled
    );
    const isDisbursing = isInQuoteStatusGroup(
      quote.Status,
      loan_quote.QuoteStatusGroup.Disbursing
    );
    const isApproved = isInQuoteStatusGroup(
      quote.Status,
      loan_quote.QuoteStatusGroup.Approved
    );
    return !isCancelled && !isDisbursing && isApproved;
  }

  const email = useClientEmail(user.clientID);
  const cancelByAxoQuoteInput = {
    CancelledBy: email.data ? getLocalPartOfEmail(email.data) : '',
    CancelledByBank: false,
  };

  const cancelByCustomerQuoteInput = {
    CancelledBy: email.data ? getLocalPartOfEmail(email.data) : '',
    CancelledByBank: false,
    OnCustomerBehalf: true,
  };
  const cancellationObjects = loanQuotes?.data
    ?.filter((quote) => canBeCancelled(quote))
    .map((quote) => {
      return {
        quoteID: quote.ID,
        input:
          cancelledBy === 'Axo'
            ? cancelByAxoQuoteInput
            : cancelByCustomerQuoteInput,
      };
    });
  const { confirm } = useConfirmDialog();
  const hasLock = lock_hooks.useCurrentUserHasLock(
    applicationID,
    marketCountry
  ).data;

  type IDialogText = {
    [K in ICancelAllOffers['cancelledBy']]: Pick<
      IDialog,
      'message' | 'confirmText' | 'cancelText'
    > & { title: string };
  };
  const dialogText: IDialogText = {
    Axo: {
      message:
        'Proceeding with this action will lead to the cancellation of all loan offers currently available to the customer. This decision is final and cannot be reversed, do you still want to continue?',
      confirmText: 'Yes, cancel all offers',
      cancelText: 'Go back',
      title: 'Cancel all by Axo',
    },
    Customer: {
      message:
        'Proceeding with this action will cancel all loan offers currently available to the customer. This decision is final and cannot be reversed, do you still want to continue?',
      confirmText: 'Yes, cancel all offers',
      cancelText: 'Go back',
      title: 'Cancel all by customer',
    },
  };

  const handleCancelAllOffers = async () => {
    confirm({
      message: dialogText[cancelledBy].message,
      confirmText: dialogText[cancelledBy].confirmText,
      cancelText: dialogText[cancelledBy].cancelText,
      confirmCallback: async () => {
        try {
          const mutations = cancellationObjects?.map(
            async (cancelledObject) => {
              await cancelQuoteByAxo.mutateAsync(cancelledObject);
              log(
                eventLogType,
                {},
                {
                  Type: KnownResourceType.LoanQuote,
                  ID: cancelledObject.quoteID,
                }
              );
            }
          );
          if (mutations) await Promise.all(mutations);
        } catch (e) {
          console.log(e);
        }
      },
    });
  };

  function getDisabledText() {
    if (cancellationObjects?.length === 0) return 'No offers to be cancelled.';
    if (!hasLock)
      return 'You can only cancel the quote when you have lock on the application.';
    return '';
  }

  const isLoading = email.isLoading || cancelQuoteByAxo.isLoading;

  return (
    <Tooltip
      disabled={!getDisabledText()}
      placement={'left'}
      content={getDisabledText()}
      fill
    >
      <MenuItem
        icon="disable"
        className={styles.redText}
        onClick={handleCancelAllOffers}
        disabled={isLoading || !!getDisabledText()}
        text={dialogText[cancelledBy].title}
      />
    </Tooltip>
  );
}
